import type Owner from '@ember/owner';
import type { ComponentLike } from '@glint/template';
import { sortByDeadline, type TaskUrgency } from 'tio-employee/utils/dashboard-kit/attention';
import type { PartnerPersona } from 'tio-employee/utils/partner-dashboard/personas';

/**
 * The sections widgets render in, in page order (Figma "Employee Home" layout).
 * Needs Your Attention sits between hero and pertinent but is not a widget
 * section: it is one merged list fed by attention sources.
 */
export const DASHBOARD_SECTIONS = ['hero', 'pertinent', 'explore'] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/** What every widget's `requires` and `load` receive. */
export interface DashboardContext {
  personas: ReadonlySet<PartnerPersona>;
  /** Current company id; empty when the session has no company. */
  companyId: string;
  /** The viewer's employee id at `companyId`; empty when unknown. Reviewer filters compare against it. */
  employeeId: string;
  /** The viewer's user id; the decision log records actors by user, not employee. */
  userId: string;
  /** The app owner, for loaders that need services (store, search, intl). */
  owner: Owner;
}

/** Signature every widget component implements; `data` is its own `load()` result. */
export interface WidgetComponentSignature<Data> {
  Args: {
    /** The resolved result of this widget's own `load()`; the frame renders the widget only once it resolves. */
    data: Data;
  };
}

export interface WidgetDefinition<Data> {
  /** Stable key; also the widget's test-hook suffix. */
  id: string;
  section: DashboardSection;
  /** The widget renders for a viewer holding any of these personas. */
  drivenBy: readonly PartnerPersona[];
  /** Extra gate, e.g. a product entitlement; the widget is skipped when this returns false. */
  requires?: (ctx: DashboardContext) => boolean;
  /** Fetches the widget's data. Called once per route visit and never awaited by the route. */
  load: (ctx: DashboardContext) => Promise<Data>;
  component: ComponentLike<WidgetComponentSignature<Data>>;
}

/** A registry entry with its data type erased, so widgets of different shapes share one list. */
export type RegisteredWidget = WidgetDefinition<unknown>;

/**
 * Registers a widget. Type-checks `load` against `component` at the definition
 * site, then erases `Data` so the entry fits the shared registry list.
 */
export function defineWidget<Data>(definition: WidgetDefinition<Data>): RegisteredWidget {
  return definition as unknown as RegisteredWidget;
}

interface PersonaGated {
  drivenBy: readonly PartnerPersona[];
  requires?: (ctx: DashboardContext) => boolean;
}

const isVisible = (entry: PersonaGated, ctx: DashboardContext) =>
  entry.drivenBy.some((persona) => ctx.personas.has(persona)) && (entry.requires?.(ctx) ?? true);

/**
 * Widgets the viewer sees, in section order and then registry order. A widget
 * is included when the viewer holds any persona in its `drivenBy` and its
 * `requires` gate (if any) passes.
 */
export function widgetsFor(
  registry: readonly RegisteredWidget[],
  ctx: DashboardContext
): RegisteredWidget[] {
  const visible = registry.filter((widget) => isVisible(widget, ctx));
  return DASHBOARD_SECTIONS.flatMap((section) =>
    visible.filter((widget) => widget.section === section)
  );
}

/** One Needs Your Attention row; the fields map onto `TaskCard`. */
export interface AttentionItem {
  /** Unique across all sources, e.g. `<source id>:<record id>`. */
  id: string;
  title: string;
  /** What to do next, or that nothing is needed. */
  description: string;
  icon: ComponentLike<{ Element: SVGElement }>;
  urgency: TaskUrgency;
  /** ISO 8601 deadline; drives the merged ordering. Undated items sort last. */
  dueAt?: string;
  /** Display form of the deadline for the chip, e.g. "due 10/14". */
  dueLabel?: string;
  /** Trailing link; set label and route together. */
  action?: {
    label: string;
    route: string;
    model?: string | number;
    query?: Record<string, unknown>;
  };
  /** The title or description names a person, so session replay masks the row. */
  pii?: boolean;
}

/** A provider of attention items, gated by persona like a widget. */
export interface AttentionSource extends PersonaGated {
  /** Stable key, used in error reporting. */
  id: string;
  /** Fetches this source's items. Called once per route visit and never awaited by the route. */
  load: (ctx: DashboardContext) => Promise<AttentionItem[]>;
}

/** The merged attention list, plus which sources loaded and which failed. */
export interface AttentionResult {
  /** Every loaded item, nearest deadline first. */
  items: AttentionItem[];
  /** How many visible sources loaded successfully, including ones that returned no items. */
  loadedSources: number;
  /**
   * Ids of sources whose `requires` gate threw, whose load threw or rejected,
   * or whose load resolved to something other than an array of well-formed
   * items; their items are missing from `items`.
   */
  failedSources: string[];
}

const URGENCIES: ReadonlySet<unknown> = new Set<TaskUrgency>(['urgent', 'soon', 'onTrack', 'info']);

// Sources are trusted code but their data isn't; a bad entry must fail its own source, not the merge.
function isAttentionItem(value: unknown): value is AttentionItem {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item['id'] === 'string' &&
    typeof item['title'] === 'string' &&
    typeof item['description'] === 'string' &&
    item['icon'] !== undefined &&
    URGENCIES.has(item['urgency']) &&
    (item['dueAt'] === undefined || typeof item['dueAt'] === 'string')
  );
}

/**
 * Loads every visible attention source in parallel and merges the items into
 * one list, nearest deadline first. When two items share an id, the first
 * source's wins. Never rejects: a source whose gate or load throws, rejects,
 * or resolves to something other than an array of well-formed items only
 * drops its own items and is reported in `failedSources`.
 */
export async function loadAttention(
  sources: readonly AttentionSource[],
  ctx: DashboardContext
): Promise<AttentionResult> {
  const settled = await Promise.allSettled(
    sources.map(async (source) => {
      if (!isVisible(source, ctx)) return null;
      const loaded: unknown = await source.load(ctx);
      // Array.from turns sparse holes into undefined; every() would skip them and let them through.
      const entries: unknown[] = Array.isArray(loaded) ? Array.from(loaded) : [];
      if (!Array.isArray(loaded) || !entries.every(isAttentionItem)) {
        throw new TypeError(`${source.id} did not return an array of attention items`);
      }
      return entries as AttentionItem[];
    })
  );

  const items: AttentionItem[] = [];
  const failedSources: string[] = [];
  let loadedSources = 0;
  const seen = new Set<string>();
  for (const [index, result] of settled.entries()) {
    const source = sources[index];
    if (!source) continue;
    if (result.status === 'rejected') {
      failedSources.push(source.id);
      continue;
    }
    if (result.value === null) continue;
    loadedSources += 1;
    for (const item of result.value) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  return { items: sortByDeadline(items, (item) => item.dueAt), loadedSources, failedSources };
}
